import React, { Fragment, useEffect, useState } from 'react';
import { Button, Checkbox, Col, Divider, Row, Typography } from 'antd';
import { UploadedFile } from '../../../../utils';
import { AuthResponse } from '../../../Auth/types';
import { ShareResponse } from '../../../../share';
import {
  CustomModal,
  CustomSelectableTable,
  CustomTabs,
  CustomTextArea,
  FormDatePicker,
  FormInput,
  FormSelect,
  UploadWithTable,
} from '../../../../components';
import { ColumnsType } from 'antd/es/table';
import { AssetsResponse } from '../../../../share/types/asset-response';
import { FileTextOutlined, UnorderedListOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import AdditionalAssets from '../components/AdditionalAssets';
import { useGetDependeciesByUserLogged } from '../../../../share';
import { useAtom } from 'jotai';
import { dataForCreate } from '../../../../state/atoms';

const { Title } = Typography;

interface TransferCreateViewProps {
  formik: any;
  goodStates?: ShareResponse[];
  supervisors?: any[];
  authData?: AuthResponse | null;
  onFiles: React.Dispatch<any>;
  files?: UploadedFile[];
  assets?: AssetsResponse[];
  onSelectNewResponsable: (data: string) => void;
  getAddittions: (assetId: string) => Promise<any>;
  selectedRowKeys?: React.Key[];
  setSelectedRowKeys?: React.Dispatch<React.SetStateAction<React.Key[]>>;
  isOpen?: boolean;
}

const TransferCreateView: React.FC<TransferCreateViewProps> = ({
  isOpen,
  formik,
  goodStates,
  supervisors,
  authData,
  files,
  onFiles,
  assets,
  onSelectNewResponsable,
  getAddittions,
  selectedRowKeys,
  setSelectedRowKeys,
}) => {
  const [formData] = useAtom(dataForCreate);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [item, setItem] = React.useState<any>([]);
  const [searchText, setSearchText] = useState('');
  const [newResponsibleId, setNewResponsibleId] = React.useState<string | null>(
    null
  );
  const [filteredData, setFilteredData] = React.useState<AssetsResponse[]>(
    assets || []
  );
  const [depNewUser, setDepNewUser] = useState<ShareResponse[]>([]);

  const { data: dependenciesUser } = useGetDependeciesByUserLogged();
  const { data: dependenciesNewUser } = useGetDependeciesByUserLogged(
    { user_id: Number(newResponsibleId) },
    { enabled: !!newResponsibleId }
  );


  const handleSelectionChange = (selectedRowKeys: React.Key[]) => {
    setSelectedRowKeys?.(selectedRowKeys);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value || '';
    setSearchText(value);
  };

  const columns: ColumnsType<AssetsResponse> = [
    {
      title: 'Item',
      dataIndex: 'id',
      key: 'id',
      filteredValue: [searchText],
      onFilter: (value, record) => {
        const val = String(value)?.toLowerCase();
        return String(record.plate_code)?.toLowerCase()?.includes(val) ||
                String(record.description)?.toLowerCase()?.includes(val) ||
                String(record.asset_class?.name)?.toLowerCase()?.includes(val) ||
                String(record.asset_class?.code)?.toLowerCase()?.includes(val);
      },
    },
    {
      title: 'Código/Placa',
      dataIndex: 'plate_code',
      key: 'plate_code',
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Clase de activo',
      dataIndex: 'asset_class',
      key: 'asset_class',
      render: (asset_class) => asset_class?.code + ' - ' + asset_class?.name,
    },
    {
      title: 'Artículo',
      dataIndex: 'item',
      key: 'item',
      render: (item) => item?.code + ' - ' + item?.name,
    },
    {
      title: 'Adiciones',
      dataIndex: 'assets_count',
      key: 'assets_count',
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (value) => (
        <Button
          disabled={!Number(value.assets_count)}
          onClick={async () => {
            let response = [];
            try {
              response = await getAddittions(value.id);
            } catch (error) {}
            setItem([{ ...value, assets: response }]);
            setModalOpen(true);
          }}
        >
          Ver adiciones
        </Button>
      ),
    },
  ];

  useEffect(() => {
    const firstDependency = dependenciesUser?.[0]?.id;
    const isDifferent = formik.values.dependency_id !== firstDependency;

    if (dependenciesUser && dependenciesUser.length > 0 && isDifferent) {
      formik.setFieldValue('dependency_id', firstDependency);
    }
  }, [dependenciesUser, formik.values.dependency_id]);
  useEffect(() => {
    const idsAssets: number[] = formData?.id_items ?? [
      Number(formData?.item?.id),
    ];
    handleSelectionChange(idsAssets);
  }, []);

  useEffect(() => {
    setFilteredData(assets || []);
  }, [assets]);
  
  useEffect(() => {
    if (isOpen) {
      setDepNewUser([]);
      formik.setFieldValue('new_dependency_id', undefined);
    }
  }, [isOpen, newResponsibleId]);

  useEffect(() => {
    if (
      isOpen &&
      dependenciesNewUser &&
      dependenciesNewUser.length > 0 &&
      newResponsibleId
    ) {
      setDepNewUser(dependenciesNewUser);
      formik.setFieldValue('new_dependency_id', dependenciesNewUser[0].id);
    }
  }, [isOpen, dependenciesNewUser, newResponsibleId]);

  return (
    <Fragment>
      <CustomModal
        title={`Ver adiciones`}
        labelCancel={`Finalizar`}
        open={modalOpen}
        width="95vw"
        onCancel={() => setModalOpen(false)}
      >
        <AdditionalAssets items={item} />
      </CustomModal>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8} lg={8}>
          <FormInput
            label="Solicitante"
            name="requester_id"
            placeholder="Solicitante"
            value={`${authData?.user.document} - ${authData?.user.name} ${authData?.user.last_name}`}
            disabled
            error={formik.errors.requester_id}
            touched={formik.touched.requester_id}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} className="input-disable">
          <FormSelect
            label="Estado solicitud"
            name="status_id"
            placeholder="Seleccione"
            value={formik.values.status_id}
            options={goodStates}
            disabled={true}
            error={formik.errors.status_id}
            touched={formik.touched.status_id}
            onChange={(value) => formik.setFieldValue('status_id', value)}
            onBlur={formik.handleBlur}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8}>
          <FormSelect
            label="Dependencia"
            name="dependency_id"
            placeholder="Seleccione"
            value={formik.values.dependency_id}
            options={dependenciesUser}
            disabled={true}
            error={formik.errors.dependency_id}
            touched={formik.touched.dependency_id}
            onChange={(value) => formik.setFieldValue('dependency_id', value)}
            onBlur={formik.handleBlur}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8}>
          <FormSelect
            label="Nuevo responsable"
            name="new_responsible_id"
            placeholder="Seleccione"
            value={formik.values.new_responsible_id}
            options={(supervisors || [])?.map((x) => ({
              name: `${x.document} - ${x?.name} ${x?.last_name}`,
              id: x?.id.toString(),
            }))}
            error={formik.errors.new_responsible_id}
            touched={formik.touched.new_responsible_id}
            onChange={(value) => {
              formik.setFieldValue('new_responsible_id', value);
              setNewResponsibleId(value);
              onSelectNewResponsable(value);
            }}
            onBlur={formik.handleBlur}
          />
        </Col>
        {formik.values.new_dependency_id && depNewUser.length > 0 && (
          <Col xs={24} sm={12} md={8} lg={8}>
            <FormSelect
              label="Dependencia nuevo responsable"
              name="new_dependency_id"
              placeholder="Seleccione"
              value={formik.values.new_dependency_id}
              options={depNewUser}
              disabled
              error={formik.errors.new_dependency_id}
              touched={formik.touched.new_dependency_id}
              onChange={(value) =>
                formik.setFieldValue('new_dependency_id', value)
              }
              onBlur={formik.handleBlur}
            />
          </Col>
        )}

        <Col xs={24} sm={12} md={8} lg={8}>
          <FormDatePicker
            label="Fecha de solicitud"
            name="query_date"
            value={formik.values.query_date || dayjs()}
            error={formik.errors.query_date}
            touched={formik.touched.query_date}
            onChange={(value) => formik.setFieldValue('query_date', value)}
            onBlur={formik.handleBlur}
            disabled
            disabledDate={(current) =>
              current && current.isBefore(dayjs(), 'day')
            }
          />
        </Col>
      </Row>
      <Row gutter={[32, 32]} style={{ marginBottom: '20px' }}>
        <Col>
          <CustomTextArea
            label="Observaciones (Opcional)"
            name="observations"
            placeholder="Observaciones"
            value={formik.values.observations}
            error={formik.errors.observations}
            touched={formik.touched.observations}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Col>
      </Row>
      <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
        <FormInput
          label="Buscador"
          placeholder="Buscar..."
          value={searchText}
          onChange={handleSearch}
          name="search"
        />
      </Col>
      <Divider />
      <CustomTabs
        defaultActiveKey="1"
        items={[
          {
            key: '1',
            label: (
              <Button type="link" icon={<UnorderedListOutlined />}>
                Items
              </Button>
            ),
            children: (
              <Fragment>
                <Row
                  justify="space-between"
                  align="middle"
                  className="mb-3 ml-1 pb-1"
                >
                  <Col>
                    <Checkbox
                      indeterminate={
                        !!selectedRowKeys &&
                        !!assets &&
                        selectedRowKeys.length > 0 &&
                        selectedRowKeys.length < assets.length
                      }
                      checked={
                        !!selectedRowKeys &&
                        !!assets &&
                        selectedRowKeys.length === assets.length
                      }
                      onChange={(e) => {
                        if (!setSelectedRowKeys || !assets) return;

                        if (e.target.checked) {
                          const allKeys = assets.map((item) => item.id);
                          setSelectedRowKeys(allKeys);
                          handleSelectionChange(allKeys);
                        } else {
                          setSelectedRowKeys([]);
                          handleSelectionChange([]);
                        }
                      }}
                    >
                      Seleccionar todos
                    </Checkbox>
                  </Col>
                </Row>
                <Row gutter={[16, 16]} justify="end">
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <CustomSelectableTable<AssetsResponse>
                      dataSource={filteredData}
                      columns={columns}
                      rowKey="id"
                      scroll={{ x: 'max-content' }}
                      rowSelection={{
                        selectedRowKeys,
                        onChange: handleSelectionChange,
                      }}
                    />
                  </Col>
                </Row>
              </Fragment>
            ),
          },
          {
            key: '2',
            label: (
              <Button type="link" icon={<FileTextOutlined />}>
                Documentos
              </Button>
            ),
            children: (
              <>
                <Title
                  level={4}
                  style={{ textAlign: 'center', marginBottom: '20px' }}
                >
                  Documentos cargados
                </Title>
                <Row>
                  <Col xs={24}>
                    <UploadWithTable onFilesChange={onFiles} files={files} />
                  </Col>
                </Row>
              </>
            ),
          },
        ]}
      />
    </Fragment>
  );
};

export default TransferCreateView;
